import * as _ from 'lodash';
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Dictionary } from 'types';

import { Color, getIconFromColor, getIconWithCrossFromColor } from 'helpers';
import { getBrandStockTooltipText } from 'app/order-shared/helpers';
import { HistoricalOrderArticle } from 'app/order-management/services/order-history/searcher.types';
import { Cell, Row, UpdatedMatrixRow } from 'app/b2b-order/components';
import { Header, PlaceholderVariant } from 'app/b2b-order/components/matrix/types';
import { MatrixType } from 'app/b2b-order/components/order/matrix/matrix.module';
import { ArticleMapConfig, MatrixLabels, OrderProduct } from './order-product-detail.service';
import { InteractiveCell, ReadyOnlyCell } from 'app/b2b-order/components/order/types';
import { MatrixIcons } from 'app/b2b-order/components/order/matrix/matrix.component';
import {
    getColorDropdownLabel,
    getCompoundSizeProductIdentifier,
    getMatrixSelectedAmount,
    getPriceForPrepack,
    getStoreLabel,
    is2DMatrix,
    isNumberedSize,
    itemHasBackorder,
    itemHasStock,
    selectedAmountIncludesBackorderStock
} from 'app/b2b-order/helpers/order';
import { getFinalPrice, isPriceDifferent } from 'app/b2b-order/helpers/price';
import { ReorderArticle, ReorderArticleSize, ReorderPrepack } from 'app/b2b-order/types/reorder';
import { OrderArticleColor, OrderArticleSize } from 'app/b2b-order/types/order';
import { PreorderArticle, PreorderArticleSize, PrepackForPreorder } from 'app/b2b-order/types/preorder';
import { sortSizes } from 'app/smart-replenishment/services/article-matrix';

export enum RowMapperType {
    Prepacks = 'prepacks',
    Sizes = 'sizes'
}

enum SizeType {
    Size = 'size',
    SubSize = 'subSize'
}

export class UiMapper {
    constructor() {}

    public mapArticleColorToItem(color: OrderArticleColor): DropdownItem {
        const formattedColor = getColorDropdownLabel(color);
        const icon = color.hasStock ? getIconFromColor(color.key) : getIconWithCrossFromColor(color.key);
        const getFallbackIcon = () =>
            color.hasStock ? getIconFromColor(Color.MULTICOLOR) : getIconWithCrossFromColor(Color.MULTICOLOR);
        return {
            text: formattedColor,
            value: color.colorGroupId,
            label: formattedColor,
            icon: icon ? icon : getFallbackIcon(),
            isCustomIcon: true
        };
    }

    public mapArticleToMatrix(
        article: ReorderArticle,
        prepackLabelPrefix: string,
        placeholderVariant = PlaceholderVariant.Floating
    ): ReorderArticle & {
        options: {
            showTotals: boolean;
            placeholderVariant: PlaceholderVariant;
        };
        dataSource: Row[];
        headers: Header[];
        verticalHeaders?: Header[];
    } {
        return {
            ...article,
            options: {
                showTotals: !!article.prepacks.length,
                placeholderVariant
            },
            ...this.mapToMatrixHeaders(article),
            dataSource: this.mapToReorderDataSource(article, prepackLabelPrefix)
        };
    }

    public mapArticleToMatrixData(
        article: OrderArticle,
        config: ArticleMapConfig
    ): OrderArticle & {
        options: { showTotals: boolean };
        matrixData: MatrixData;
    } {
        return {
            ...article,
            options: { showTotals: _.isEmpty(article.prepacks) },
            matrixData: this.mapToOrderMatrixData(article, config)
        };
    }

    public mapPreorderArticleToMatrix(
        article: PreorderArticle,
        prepackLabelPrefix: string,
        placeholderVariant = PlaceholderVariant.Fixed
    ): PreorderArticle & {
        options: { showTotals: boolean; placeholderVariant: PlaceholderVariant };
        dataSource: Row[];
    } {
        return {
            ...article,
            options: {
                showTotals: !!article.prepacks.length,
                placeholderVariant
            },
            ...this.mapToMatrixHeaders(article),
            dataSource: this.mapToPreorderDataSource(article, prepackLabelPrefix)
        };
    }

    public mapUpdatedArticleRow(article: ReorderArticle, updatedRow: UpdatedMatrixRow): ReorderArticle {
        const { itemType, updatedItems } = this.mapUpdatedItemsAndType(article, updatedRow);
        return {
            ...article,
            [itemType]: updatedItems,
            stock: {
                ...article.stock,
                validSelectedAmount: updatedRow.status.isValid && updatedRow.matrixStatus.isValid
            }
        };
    }

    public mapUpdatedPreorderArticleRow(article: PreorderArticle, updatedRow: UpdatedMatrixRow): PreorderArticle {
        const { itemType, updatedItems } = this.mapUpdatedItemsAndType(article, updatedRow);
        return {
            ...article,
            [itemType]: updatedItems,
            stock: {
                validSelectedAmount: updatedRow.status.isValid && updatedRow.matrixStatus.isValid
            }
        };
    }

    public historyArticleToMatrixSizeRowMapper(article: HistoricalOrderArticle): Row {
        return (article.sizes as (PreorderArticleSize | ReorderArticleSize)[]).reduce(
            (singleSizesRow, size) => ({
                type: RowMapperType.Sizes,
                ...singleSizesRow,
                cells: {
                    ...singleSizesRow.cells,
                    [size.gtin]: {
                        placeholder: '',
                        value: getMatrixSelectedAmount(size.stock.selectedAmount) || '',
                        hasTooltip:
                            itemHasStock(size as ReorderArticleSize) &&
                            (isPriceDifferent(size.price, article.price) ||
                                itemHasBackorder(size as ReorderArticleSize)),
                        inputOptions: {
                            blocked: true
                        }
                    }
                }
            }),
            {} as Row
        );
    }

    private articleToMatrixSizeRowMapper(article: ReorderArticle): Row {
        return article.sizes.reduce(
            (singleSizesRow, size) => ({
                type: RowMapperType.Sizes,
                ...singleSizesRow,
                cells: {
                    ...singleSizesRow.cells,
                    [size.gtin]: {
                        placeholder: this.calculateStockPlaceholder(size),
                        value: getMatrixSelectedAmount(size.stock.selectedAmount) || '',
                        hasTooltip:
                            itemHasStock(size) &&
                            (isPriceDifferent(size.price, article.price) || itemHasBackorder(size)),
                        isMarked: selectedAmountIncludesBackorderStock(
                            getMatrixSelectedAmount(size.stock.selectedAmount),
                            size.stock
                        ),
                        inputOptions: {
                            validators: [Validators.min(0), Validators.max(size.stock.total)],
                            e2eHooks: {
                                input: 'fc-product-matrix-' + size.gtin,
                                placeholder: 'pdp-order-matrix-available-stock-for-' + size.gtin
                            }
                        }
                    }
                }
            }),
            {} as Row
        );
    }

    private articleTo2DMatrixRowMapper(article: ReorderArticle): Row[] {
        const articleSizes = article.sizes;
        const subSizes = _.uniq(_.map(articleSizes, 'detailedSize.subSize'));
        const mainSizes = _.uniq(_.map(articleSizes, 'detailedSize.size'));
        const sizeMap: CompoundSizeMap = articleSizes.reduce(
            (map, size) => ({
                ...map,
                [`${size.detailedSize.size}/${size.detailedSize.subSize}`]: size
            }),
            {}
        );
        const rows: Row[] = subSizes.map((subSize) => ({
            type: RowMapperType.Sizes,
            key: subSize,
            cells: mainSizes.reduce(
                (sizesRow, mainSize: string) => ({
                    ...sizesRow,
                    [`${mainSize}/${subSize}`]: this.mapSizeToCell({ mainSize, subSize, article, sizeMap })
                }),
                {}
            )
        }));
        return rows;
    }

    private preorderArticleTo2DMatrixRowMapper(article: PreorderArticle): Row[] {
        const articleSizes = article.sizes;
        const subSizes = _.uniq(_.map(articleSizes, 'detailedSize.subSize'));
        const mainSizes = _.uniq(_.map(articleSizes, 'detailedSize.size'));
        const sizeMap: CompoundSizeMap = articleSizes.reduce(
            (map, size) => ({
                ...map,
                [`${size.detailedSize.size}/${size.detailedSize.subSize}`]: size
            }),
            {}
        );
        const rows: Row[] = subSizes.map((subSize) => ({
            type: RowMapperType.Sizes,
            key: subSize,
            cells: mainSizes.reduce(
                (sizesRow, mainSize: string) => ({
                    ...sizesRow,
                    [`${mainSize}/${subSize}`]: this.mapPreorderSizeToCell({ mainSize, subSize, article, sizeMap })
                }),
                {}
            )
        }));
        return rows;
    }

    private prepackToMatrixPrepackRowMapper(
        prepack: ReorderPrepack,
        sizes: Pick<ReorderArticleSize, 'gtin'>[],
        prepackLabelPrefix?: string
    ): Row {
        return {
            type: RowMapperType.Prepacks,
            label: prepackLabelPrefix ? `${prepackLabelPrefix} ${prepack.name}` : prepack.name,
            zeroth: {
                key: prepack._id,
                value: getMatrixSelectedAmount(prepack.stock.selectedAmount) || '',
                hasTooltip: itemHasStock(prepack) && itemHasBackorder(prepack),
                isMarked: selectedAmountIncludesBackorderStock(
                    getMatrixSelectedAmount(prepack.stock.selectedAmount),
                    prepack.stock
                ),
                placeholder: prepack.stock.total.toString(),
                inputOptions: {
                    validators: [Validators.min(0), Validators.max(prepack.stock.total)],
                    e2eHooks: {
                        input: 'fc-prepack-matrix-' + prepack._id,
                        placeholder: 'pdp-order-matrix-available-stock-for-' + prepack._id
                    }
                }
            },
            cells: [...prepack.products, ..._.differenceBy(sizes, prepack.products, 'gtin')].reduce(
                (prepackQuantityCells, { gtin, quantity }: { gtin: string; quantity?: number }) => ({
                    ...prepackQuantityCells,
                    [gtin]: {
                        value: quantity || '',
                        placeholder: quantity ? undefined : '-',
                        inputOptions: {
                            disabled: true
                        }
                    }
                }),
                {}
            )
        };
    }

    private preorderArticleToMatrixSizeRowMapper(article: PreorderArticle): Row {
        return {
            type: RowMapperType.Sizes,
            cells: article.sizes.reduce(
                (rowCells, size) => ({
                    ...rowCells,
                    [size.gtin]: {
                        value: size.stock.selectedAmount || '',
                        placeholder: '0',
                        hasTooltip: isPriceDifferent(size.price, article.price),
                        inputOptions: {
                            validators: [Validators.min(0)],
                            e2eHooks: {
                                input: 'fc-product-matrix-' + size.gtin,
                                placeholder: 'pdp-order-matrix-available-stock-for-' + size.gtin
                            }
                        }
                    }
                }),
                {} as Dictionary<Cell>
            )
        };
    }

    private preorderPrepackToMatrixPrepackRowMapper(
        prepack: PrepackForPreorder,
        sizes: Pick<PreorderArticleSize, 'gtin'>[],
        prepackLabelPrefix?: string
    ): Row {
        return {
            type: RowMapperType.Prepacks,
            label: prepackLabelPrefix ? `${prepackLabelPrefix} ${prepack.name}` : prepack.name,
            zeroth: {
                key: prepack._id,
                value: prepack.stock.selectedAmount || '',
                placeholder: '0',
                inputOptions: {
                    validators: [Validators.min(0)],
                    e2eHooks: {
                        input: 'fc-prepack-matrix-' + prepack._id,
                        placeholder: 'pdp-order-matrix-available-stock-for-' + prepack._id
                    }
                }
            },
            cells: [...prepack.products, ..._.differenceBy(sizes, prepack.products, 'gtin')].reduce(
                (prepackQuantityCells, { gtin, quantity }: { gtin: string; quantity?: number }) => ({
                    ...prepackQuantityCells,
                    [gtin]: {
                        value: quantity || '',
                        placeholder: quantity ? undefined : '-',
                        inputOptions: {
                            disabled: true
                        }
                    }
                }),
                {}
            )
        };
    }

    private mapUpdatedItemsAndType(
        article: ReorderArticle | PreorderArticle,
        updatedRow: UpdatedMatrixRow
    ): { itemType: RowMapperType; updatedItems: (ReorderArticle | PreorderArticle)[] } {
        const use2DMatrix = is2DMatrix(article);

        const itemMapper = (type: RowMapperType) => (item) => {
            const getProductIdentifier = (product) =>
                use2DMatrix ? getCompoundSizeProductIdentifier(product) : product.gtin;

            const identifier =
                type === RowMapperType.Prepacks ? (item as ReorderPrepack)._id : getProductIdentifier(item);
            const hasItemBeenUpdated = _.has(updatedRow.row, identifier);

            return hasItemBeenUpdated
                ? {
                      ...item,
                      stock: {
                          ...item.stock,
                          selectedAmount: parseInt(updatedRow.row[identifier], 10) || 0
                      }
                  }
                : item;
        };

        const itemType = use2DMatrix ? RowMapperType.Sizes : updatedRow.type;
        const updatedItems = _.map(article[itemType], itemMapper(itemType));

        return {
            itemType,
            updatedItems
        };
    }

    private mapToMatrixHeaders(article: ReorderArticle | PreorderArticle): {
        headers: Header[];
        verticalHeaders?: Header[];
    } {
        return {
            ...(is2DMatrix(article)
                ? {
                      headers: this.mapTo2DHeaders(article.sizes, SizeType.Size),
                      verticalHeaders: this.mapTo2DHeaders(article.sizes, SizeType.SubSize)
                  }
                : { headers: this.mapToHeaders(article.sizes) })
        };
    }

    private mapToHeaders(sizes: (ReorderArticleSize | PreorderArticleSize)[]): Header[] {
        return sizes.map((size) => ({
            text: size.size,
            key: size.gtin
        }));
    }

    private mapTo2DHeaders(articleSizes: (ReorderArticleSize | PreorderArticleSize)[], sizeType: SizeType): Header[] {
        return _.chain(articleSizes)
            .map((size) => ({
                text: size.detailedSize[sizeType].toString(),
                key: size.detailedSize[sizeType].toString()
            }))
            .sortBy('text')
            .uniqBy('text')
            .value();
    }

    private mapToReorderDataSource(article: ReorderArticle, prepackLabelPrefix: string): Row[] {
        if (is2DMatrix(article)) {
            return this.articleTo2DMatrixRowMapper(article);
        }

        return [
            this.articleToMatrixSizeRowMapper(article),
            ...article.prepacks.map((prepack) =>
                this.prepackToMatrixPrepackRowMapper(prepack, article.sizes, prepackLabelPrefix)
            )
        ];
    }

    private mapToOrderMatrixData(article: OrderArticle, config: ArticleMapConfig): MatrixData {
        const matrixData: MatrixData = {
            type: MatrixType.Unsupported,
            dataSource: {
                rows: [],
                multiStoreRows: {
                    prepacks: [],
                    articles: []
                }
            }
        };

        const isMultiStore = (config.stores || []).length > 1;

        if (isMultiStore) {
            matrixData.type = MatrixType.Compound;

            if (article.prepacks.length) {
                matrixData.dataSource.multiStoreRows.prepacks.push(
                    ...this.mapPrepacksToMatrixRows(article, { ...config, showStoreStock: false })
                );
            }

            if (article.sizes.length) {
                matrixData.dataSource.multiStoreRows.articles.push(...this.map1DMatrixRows(article, config));
            }

            return matrixData;
        }

        if (article.prepacks.length > 0) {
            matrixData.type = MatrixType.Prepacks;
            matrixData.dataSource.rows.push(
                ...this.mapPrepacksToMatrixRows(article, { ...config, showStoreStock: false })
            );
        }

        if (article.sizes.length > 0) {
            const is2DArticle = is2DMatrix(article);
            matrixData.type = is2DArticle ? MatrixType.Compound : MatrixType.SingleSize;

            if (isMultiStore) {
                matrixData.type = MatrixType.Compound;
            }

            if (config.isShoppingCart) {
                matrixData.dataSource.rows.push(
                    ...(is2DArticle
                        ? this.mapShoppingCartMatrixRows(article, config)
                        : this.map1DMatrixRows(article, config))
                );
            } else {
                matrixData.dataSource.rows.push(
                    ...(is2DArticle ? this.map2DMatrixRows(article, config) : this.map1DMatrixRows(article, config))
                );
            }
        }

        return matrixData;
    }

    private mapPrepacksToMatrixRows(
        article: OrderArticle,
        config: ArticleMapConfig
    ): Array<Array<InteractiveCell | ReadyOnlyCell>> {
        const isArticleWithCompoundSize = is2DMatrix(article);
        const isMultiStore = config.stores && config.stores.length > 1;

        let prepacks = article.prepacks as ReorderPrepack[];

        if (config.showAvailableOnly) {
            prepacks = prepacks.filter((prepack) => prepack.stock.total || prepack.stock.selectedAmount);
        }

        return prepacks.map((prepack: ReorderPrepack) => {
            const formattedPrepackProducts = prepack.products.map((product) => {
                const { quantity, detailedSize, size } = product;

                return {
                    text: isArticleWithCompoundSize ? `${detailedSize.size} L${detailedSize.subSize}` : `${size}`,
                    quantity
                };
            });

            const disabled =
                config.productType === OrderProduct.Reorder
                    ? !(prepack.stock?.available || prepack.stock?.backorder)
                    : false;

            let prepackInput: InteractiveCell = {
                id: prepack._id,
                leftLabel: config.prepackLabelPrefix ? `${config.prepackLabelPrefix} ${prepack.name}` : prepack.name,
                selectedQuantity: prepack.stock?.selectedAmount || null,
                availableQuantity: prepack.stock?.available,
                isAvailableQuantityVisible: config.productType === OrderProduct.Reorder,
                disabled,
                validators: this.getValidators(prepack.stock?.total, config.productType),
                inputTooltip: this.getInputTooltipText(prepack, article, config),
                iconTooltip: this.getIconTooltipText(prepack, config),
                e2eHooks: {
                    input: 'fc-product-matrix-' + prepack._id,
                    availableQuantity: prepack.stock?.total?.toString()
                },
                price: {
                    currency: _.get(prepack, 'products[0].price.currency'),
                    purchasePrice: getPriceForPrepack(article, {
                        ...prepack,
                        stock: { ...prepack.stock, selectedAmount: 1 }
                    }),
                    recommendedRetailPrice: 0
                }
            };

            prepackInput = this.handleCellTransformations(prepackInput, config);

            if (isMultiStore) {
                return [...this.mapMultiStoreCell(prepackInput, config)];
            } else if (config.isShoppingCart) {
                return [prepackInput];
            }

            const prepackLabels: ReadyOnlyCell = { items: formattedPrepackProducts };
            return [prepackInput, prepackLabels] as [InteractiveCell, ReadyOnlyCell];
        });
    }

    private map2DMatrixRows(article, config: ArticleMapConfig): (ReadyOnlyCell | InteractiveCell)[][] {
        const articleSizes = config.showAvailableOnly
            ? article.sizes.filter((size) => itemHasStock(size) || size.stock.selectedAmount)
            : article.sizes;

        const subSizes = sortSizes(_.uniq(_.map(articleSizes, 'detailedSize.subSize')));
        const mainSizes = sortSizes(_.uniq(_.map(articleSizes, 'detailedSize.size')));

        const sizeMap: CompoundSizeMap = articleSizes.reduce(
            (map, size) => ({
                ...map,
                [`${size.detailedSize.size}/${size.detailedSize.subSize}`]: size
            }),
            {}
        );

        return subSizes.map((subSize) => {
            const cells = [];

            mainSizes.forEach((mainSize: string) => {
                const size = sizeMap[`${mainSize}/${subSize}`];

                if (!size) {
                    if (config.showStoreStock) {
                        const storeGln = _.get(config, 'stores.0.address.gln');
                        const retailerStockCell = this.getRetailerStockCell(size, subSize, storeGln, config.labels);

                        cells.push(retailerStockCell, null);
                    } else {
                        cells.push(null);
                    }

                    return;
                }

                if (config.showStoreStock) {
                    const storeGln = _.get(config, 'stores.0.address.gln');
                    const interactiveCell = this.mapSizeToMatrixCell(size, article, config);
                    const retailerStockCell = this.getRetailerStockCell(
                        size,
                        interactiveCell.leftLabel,
                        storeGln,
                        config.labels
                    );

                    cells.push(retailerStockCell, interactiveCell);
                } else {
                    cells.push(this.mapSizeToMatrixCell(size, article, config));
                }
            });

            return cells;
        });
    }

    private mapShoppingCartMatrixRows(article, config: ArticleMapConfig): (ReadyOnlyCell | InteractiveCell)[][] {
        return article.sizes.map((size) => [this.mapSizeToMatrixCell(size, article, config)]);
    }

    private mapSizeToMatrixCell(size, article: OrderArticle, config: ArticleMapConfig): InteractiveCell {
        const is2DArticle = is2DMatrix(article);
        const hasStock = size.stock?.available || size.stock?.backorder;

        const validators = this.getValidators(size.stock?.total, config.productType);

        if (size.orderIncrement) {
            validators.push(incrementValidator(size.orderIncrement));
        }

        const interactiveCell: Partial<InteractiveCell> = {
            id: size.gtin,
            availableQuantity: size.stock?.total,
            inmidiateStock: size.stock?.available,
            selectedQuantity: size.stock?.selectedAmount || null,
            iconTooltip: this.getIconTooltipText(size, config),
            inputTooltip: this.getInputTooltipText(size, article, config),
            disabled: config.productType === OrderProduct.Preorder ? false : !hasStock,
            e2eHooks: {
                input: 'fc-product-matrix-' + size.gtin,
                availableQuantity: size.stock?.total?.toString()
            },
            isAvailableQuantityVisible: config.productType === OrderProduct.Reorder,
            price: size.price,
            topLabel: 'Qty',
            validators
        };

        const isMultiStore = config.stores && config.stores.length > 1;

        const getSimplifiedSize = (fullSizeData) =>
            fullSizeData.detailedSize.subSize
                ? `${fullSizeData.detailedSize.size}/${fullSizeData.detailedSize.subSize}`
                : fullSizeData.detailedSize.size;

        if (is2DArticle) {
            const mainSize = _.get(size, 'detailedSize.size');
            const subSize = _.get(size, 'detailedSize.subSize');
            const hasNumberSizes = [mainSize, subSize].every(isNumberedSize);

            interactiveCell.topLabel = size.detailedSize?.size || size.size;
            interactiveCell.leftLabel = subSize;

            if (config.isShoppingCart || isMultiStore) {
                interactiveCell.leftLabel = hasNumberSizes ? `W${mainSize} L${subSize}` : `${mainSize} ${subSize}`;
                interactiveCell.topLabel = '';
            }
        } else {
            interactiveCell.leftLabel =
                size.detailedSize && size.detailedSize.size !== '' && article.prepacks?.length === 0
                    ? getSimplifiedSize(size)
                    : size.size;
        }

        return this.handleCellTransformations(interactiveCell as InteractiveCell, config, size);
    }

    private getInputTooltipText(size, article, config: ArticleMapConfig) {
        if (!isPriceDifferent(size.price, article.price)) return;
        const price = getFinalPrice(size, !!article.price.discountedPurchasePrice, config.userLanguage);
        return this.getLabel(config, MatrixLabels.PriceWarning).replace('{{price}}', price);
    }

    private getIconTooltipText(
        item: ReorderPrepack | ReorderArticleSize,
        config: ArticleMapConfig
    ): { available: string; backorder: string } | undefined {
        const isBackorderStockZero = ({ stock }: Pick<ReorderArticleSize, 'stock'>) => stock.backorder?.available === 0;

        if (config.productType === OrderProduct.Preorder || !itemHasBackorder(item) || isBackorderStockZero(item))
            return;

        const labels = {
            available: this.getLabel(config, MatrixLabels.AvailableStock),
            backorder: this.getLabel(config, MatrixLabels.BackorderMultiWeek),
            backorderSingular: this.getLabel(config, MatrixLabels.BackorderMultiWeekSingular)
        };

        return getBrandStockTooltipText(item.stock, labels);
    }

    private getValidators(maxValue, productType: OrderProduct): ValidatorFn[] {
        const commonValidators = [Validators.min(0)];

        if (productType === OrderProduct.Preorder) {
            return commonValidators;
        }

        return [...commonValidators, Validators.max(maxValue)];
    }

    private map1DMatrixRows(article, config: ArticleMapConfig) {
        let sizes = article.sizes;

        if (config.showAvailableOnly) {
            sizes = sizes.filter((size) => itemHasStock(size) || size.stock.selectedAmount);
        }

        return _.map(sizes, (size) =>
            this.mapMultiStoreCell(this.mapSizeToMatrixCell(size, article, config), config, size)
        );
    }

    private mapMultiStoreCell(
        cell: InteractiveCell,
        config: ArticleMapConfig,
        size?: ReorderArticleSize
    ): Array<InteractiveCell | ReadyOnlyCell> {
        const { stores, isShoppingCart, showStoreStock } = config;
        const isMultiStore = stores && stores.length > 1;
        const GLNs = (config.stores || []).map((store) => store.address.gln);

        if (!isMultiStore || isShoppingCart) {
            if (showStoreStock) {
                return [this.getRetailerStockCell(size, cell.leftLabel, GLNs[0], config.labels), cell];
            } else {
                return [cell];
            }
        }

        const storeMap = {};

        stores.forEach((store) => {
            storeMap[store._id] = {
                name: getStoreLabel(store),
                gln: store.address.gln
            };
        });

        const brandStockCell: InteractiveCell = {
            ...cell,
            disabled: true,
            topLabel: 'ORDER.ORDER_MATRIX.BRAND_STOCK',
            selectedQuantity: cell.availableQuantity
        };

        const cells: Array<InteractiveCell | ReadyOnlyCell> = [brandStockCell];

        Object.keys(storeMap).forEach((storeId) => {
            const { name, gln } = storeMap[storeId];
            const storeCell = _.omit(cell, 'iconTooltip');

            storeCell.selectedQuantity = cell.selectedQuantity[storeId] || null;
            storeCell.topLabel = name;
            storeCell.storeId = storeId;
            storeCell.e2eHooks.input = `${cell.e2eHooks.input}-${storeId}`;

            const totalSelected = _.sum(Object.values(cell.selectedQuantity).filter((value) => value !== null));
            const selfSelected = (storeCell.selectedQuantity as number) || 0;
            const maxValue = cell.availableQuantity - totalSelected + selfSelected;

            storeCell.validators = this.getValidators(maxValue, OrderProduct.Reorder);

            if (size && size.orderIncrement) {
                storeCell.validators.push(incrementValidator(size.orderIncrement));
            }

            if (showStoreStock) {
                cells.push(this.getRetailerStockCell(size, storeCell.leftLabel, gln, config.labels));
            }

            cells.push(storeCell);
        });

        return cells;
    }

    private getRetailerStockCell(
        size: ReorderArticleSize,
        leftLabel: InteractiveCell['leftLabel'],
        gln: string,
        labels: ArticleMapConfig['labels']
    ): ReadyOnlyCell {
        const storeStock = _.get(size, `stock.storeStock.${gln}`);
        const hasRetailerStock = !_.isNil(storeStock);

        const readOnlyCell = {
            items: [{ text: hasRetailerStock ? String(storeStock) : '-' }],
            topLabel: MatrixIcons.Shop,
            leftLabel,
            tooltip: {
                cell: hasRetailerStock
                    ? labels[MatrixLabels.InventoryAvailable]
                    : labels[MatrixLabels.InventoryUnavailable],

                topLabel: labels[MatrixLabels.InventoryAvailable]
            }
        };

        return readOnlyCell;
    }

    private mapToPreorderDataSource(article: PreorderArticle, prepackLabelPrefix: string): Row[] {
        if (is2DMatrix(article)) {
            return this.preorderArticleTo2DMatrixRowMapper(article);
        }
        return [
            ...(article.isOrderableAsSingleSize ? [this.preorderArticleToMatrixSizeRowMapper(article)] : []),
            ...article.prepacks.map((prepack) =>
                this.preorderPrepackToMatrixPrepackRowMapper(prepack, article.sizes, prepackLabelPrefix)
            )
        ];
    }

    private calculateStockPlaceholder(size: ReorderArticleSize): string {
        const isOnlyBackorderStock = size.stock.available === 0 && itemHasBackorder(size);
        return itemHasStock(size) && isOnlyBackorderStock
            ? `[${size.stock.backorder.available}]`
            : size.stock.total.toString();
    }

    private mapSizeToCell({
        mainSize,
        subSize,
        article,
        sizeMap
    }: {
        mainSize: string;
        subSize: string;
        article: ReorderArticle;
        sizeMap: CompoundSizeMap;
    }) {
        const size = sizeMap[`${mainSize}/${subSize}`];
        return size
            ? {
                  gtin: size.gtin,
                  placeholder: this.calculateStockPlaceholder(size),
                  value: size.stock.selectedAmount || '',
                  hasTooltip:
                      itemHasStock(size) && (isPriceDifferent(size.price, article.price) || itemHasBackorder(size)),
                  isMarked: selectedAmountIncludesBackorderStock(
                      getMatrixSelectedAmount(size.stock.selectedAmount),
                      size.stock
                  ),
                  inputOptions: {
                      validators: [Validators.min(0), Validators.max(size.stock.total)],
                      e2eHooks: {
                          input: 'fc-product-matrix-' + size.gtin,
                          placeholder: 'pdp-order-matrix-available-stock-for-' + size.gtin
                      }
                  }
              }
            : {
                  placeholder: '-',
                  value: '',
                  isMarked: false,
                  inputOptions: {
                      disabled: true
                  }
              };
    }

    private handleCellTransformations(
        cell: InteractiveCell,
        config: ArticleMapConfig,
        size?: OrderArticleSize
    ): InteractiveCell {
        const normalizedCell = _.cloneDeep(cell);
        const { stores, isShoppingCart } = config;
        const isMultiStore = stores && stores.length > 1;

        if (isMultiStore) {
            const initialValue = {};

            stores.forEach((store) => {
                initialValue[store._id] = null;
            });

            if (isShoppingCart) {
                const totalSelected = _.get(config.selectedQuantityMap, cell.id, 0);
                const selfSelected = (cell.selectedQuantity as number) || 0;
                const maxValue = cell.availableQuantity - totalSelected + selfSelected;

                normalizedCell.validators = this.getValidators(maxValue, OrderProduct.Reorder);

                if (size && size.orderIncrement) {
                    normalizedCell.validators.push(incrementValidator(size.orderIncrement));
                }
            } else {
                normalizedCell.isAvailableQuantityVisible = false;
                normalizedCell.selectedQuantity = normalizedCell.selectedQuantity || initialValue;
            }
        }

        return normalizedCell;
    }

    private mapPreorderSizeToCell({
        mainSize,
        subSize,
        article,
        sizeMap
    }: {
        mainSize: string;
        subSize: string;
        article: PreorderArticle;
        sizeMap: CompoundSizeMap;
    }) {
        const size = sizeMap[`${mainSize}/${subSize}`];

        return size
            ? {
                  gtin: size.gtin,
                  placeholder: 0,
                  value: size.stock.selectedAmount || '',
                  hasTooltip: isPriceDifferent(size.price, article.price),
                  isMarked: selectedAmountIncludesBackorderStock(
                      getMatrixSelectedAmount(size.stock.selectedAmount),
                      size.stock
                  ),
                  inputOptions: {
                      validators: [Validators.min(0)],
                      e2eHooks: {
                          input: 'fc-product-matrix-' + size.gtin,
                          placeholder: 'pdp-order-matrix-available-stock-for-' + size.gtin
                      }
                  }
              }
            : {
                  placeholder: '-',
                  value: '',
                  isMarked: false,
                  inputOptions: {
                      disabled: true
                  }
              };
    }

    private getLabel(config: ArticleMapConfig, key: string) {
        return config?.labels?.[key] || key;
    }
}

interface CompoundSizeMap {
    [compoundSize: string]: ReorderArticleSize;
}

interface DropdownItem {
    text: string;
    value: string;
    label: string;
    icon?: string;
    isCustomIcon?: boolean;
}

export interface MatrixData {
    dataSource: DataSource;
    type: MatrixType;
}

export interface DataSource {
    rows: Array<Array<InteractiveCell | ReadyOnlyCell | null>>;
    multiStoreRows: {
        prepacks: Array<Array<InteractiveCell | ReadyOnlyCell | null>>;
        articles: Array<Array<InteractiveCell | ReadyOnlyCell | null>>;
    };
}

type OrderArticle = ReorderArticle | PreorderArticle;

export enum OrderableAs {
    ProductsAndPrepacks = 'products_and_prepacks',
    OnlyPrepacks = 'only_prepacks',
    OnlyProducts = 'only_products'
}

export function incrementValidator(increment: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        let inputValue = Number(control.value);
        inputValue = isNaN(inputValue) ? 0 : inputValue;

        const isValid = inputValue % increment === 0;
        return !isValid ? { [MatrixErrorTypes.INVALID_INCREMENT]: { actual: control.value, increment } } : null;
    };
}

export enum MatrixErrorTypes {
    INVALID_INCREMENT = 'invalidIncrement',
    INVALID_STOCK = 'max'
}
